import React, { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import { EChart, type EChartProps } from './EChart';
import {
  getCompactGrid,
  getFocusCartesianGrid,
  getPreviewGrid,
  type ChartDensity,
} from './chartDensity';

export interface TimeSeriesSeries {
  name: string;
  data: number[];
  type?: 'line' | 'bar';
  smooth?: boolean;
}

/** Compact number ticks on value axes (focus mode). */
export function compactAxisNumberLabel(value: number): string {
  const v = Number(value);
  if (!Number.isFinite(v)) return '';
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
  if (a >= 10_000) return `${(v / 1_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`;
  if (Number.isInteger(v)) return String(v);
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function shortPreviewDateLabel(value: string): string {
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    const mo = Number(m);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[Math.max(0, mo - 1)] ?? m} '${String(y).slice(-2)}`;
  }
  if (/^\d{4}-\d{2}$/.test(s)) return s.replace('-', '‑');
  return s.length > 14 ? `${s.slice(0, 12)}…` : s;
}

export interface TimeSeriesLineChartProps {
  /** X-axis labels (e.g. dates or period strings) */
  xAxisData: string[];
  /** One or more series (name + values aligned to xAxisData) */
  series: TimeSeriesSeries[];
  height?: number | string;
  /** Optional ECharts option overrides */
  optionOverrides?: Partial<EChartsOption>;
  showDataLabels?: boolean;
  /** overview = cockpit tiles; preview = catalogue cards; focus = analytics focus pages */
  density?: ChartDensity;
  /** Forward ECharts DOM events (e.g. chart click on a bar category) */
  onEvents?: EChartProps['onEvents'];
  /**
   * When the primary series is `bar`, the category (xAxis label) to emphasize (e.g. selected day).
   */
  barHighlightCategory?: string | null;
}

/**
 * Generic time-series line (or multi-line/bar) chart using ECharts.
 * Reuses EChart and theme; accepts xAxisData and series for planned/actual, trend metrics, etc.
 */
export const TimeSeriesLineChart: React.FC<TimeSeriesLineChartProps> = ({
  xAxisData,
  series,
  height = 260,
  optionOverrides,
  showDataLabels = true,
  density = 'overview',
  onEvents,
  barHighlightCategory,
}) => {
  const compactGrid = getCompactGrid(false);
  const option = useMemo<EChartsOption>(() => {
    const n = xAxisData.length;
    const maxLabels = density === 'preview' ? 5 : density === 'overview' ? 12 : 24;
    const step = n <= maxLabels ? 1 : Math.max(1, Math.ceil(n / maxLabels));

    const effectiveShowLabels = density === 'preview' ? false : showDataLabels;
    const allLines = series.length > 0 && series.every((s) => (s.type ?? 'line') === 'line');
    /** Catalogue cards: silhouette only — single-series legend hidden; 2-series keep compact legend */
    const showLegend =
      density === 'preview'
        ? series.length >= 2
        : series.length > 1;
    /** Preview line charts: only start / midpoint / end on X (silhouette; no dense tick diagnostic) */
    const previewTickStartMidEnd =
      density === 'preview' && allLines && n > 6;

    const previewGrid = getPreviewGrid(false);
    let grid: { top: number; right: number; bottom: number; left: number; containLabel: boolean };
    let extraSliderBottom = 0;
    const useDataZoom = density === 'focus' && n > 72;

    if (density === 'preview') {
      grid = { ...previewGrid, bottom: showLegend ? Math.max(previewGrid.bottom, 44) : previewGrid.bottom };
    } else if (density === 'overview') {
      grid = {
        ...compactGrid,
        top: Math.max(compactGrid.top, showLegend ? 28 : 24),
        bottom: Math.max(compactGrid.bottom, showLegend ? 72 : 64),
      };
    } else {
      extraSliderBottom = useDataZoom ? 28 : 0;
      grid = getFocusCartesianGrid(extraSliderBottom);
    }

    const axisFontSize = density === 'preview' ? 9 : density === 'overview' ? 10 : 11;

    const tooltipPreview = { show: false } satisfies EChartsOption['tooltip'];

    const echartsSeries = series.map((s) => {
      const resolvedType = s.type ?? 'line';
      const isLine = resolvedType === 'line';
      const baseColor = resolvedType === 'bar' ? '#3B82F6' : undefined;
      const barColor =
        resolvedType === 'bar' && barHighlightCategory
          ? (params: { dataIndex?: number }) => {
              const idx = params.dataIndex ?? -1;
              const cat = xAxisData[idx] ?? '';
              return cat === barHighlightCategory ? '#1D4ED8' : (baseColor ?? '#3B82F6');
            }
          : undefined;
      return {
        name: s.name,
        type: resolvedType,
        smooth: density === 'preview' ? false : (s.smooth ?? true),
        large: density === 'focus' && resolvedType === 'bar' && n > 80 ? true : undefined,
        progressive: density === 'focus' && resolvedType === 'bar' && n > 80 ? 400 : undefined,
        data: s.data,
        showSymbol: isLine ? (density === 'preview' ? false : density === 'focus' ? n <= 24 : n <= 28) : false,
        symbolSize: density === 'focus' ? 5 : 6,
        lineStyle: isLine && density !== 'overview' ? { width: density === 'preview' ? 1.5 : 2 } : undefined,
        ...(isLine && density !== 'overview'
          ? {
              emphasis: { focus: 'series' as const, scale: false },
            }
          : {}),
        ...(resolvedType === 'bar'
          ? {
              itemStyle: barColor ? { color: barColor } : { color: baseColor },
            }
          : {}),
        ...(effectiveShowLabels && resolvedType !== 'line'
          ? {
              label: {
                show: true,
                position: 'top' as const,
                fontSize: 9,
                formatter: (params: { dataIndex: number; value: unknown }) =>
                  params.dataIndex % step === 0 ? String(params.value) : '',
              },
            }
          : effectiveShowLabels && isLine
            ? {
                label: {
                  show: true,
                  position: 'top' as const,
                  formatter: (params: { dataIndex: number; value: unknown }) =>
                    params.dataIndex % step === 0 ? String(params.value) : '',
                },
              }
            : {}),
      };
    });

    const previewHideAllXLabels = density === 'preview' && allLines && n > 32;
    const previewBarSparse =
      density === 'preview' && !allLines && n > 8;

    const xAxisBase = {
      type: 'category' as const,
      data: xAxisData,
      boundaryGap: false as const,
      axisLabel: previewHideAllXLabels
        ? { show: false as const }
        : {
            fontSize: axisFontSize,
            rotate: density === 'preview'
              ? previewTickStartMidEnd || previewBarSparse
                ? 0
                : n > 10
                  ? 24
                  : 0
              : density === 'focus' && n > maxLabels
                ? 28
                : 0,
            hideOverlap: !previewTickStartMidEnd,
            ...(previewTickStartMidEnd
              ? {
                  formatter: (value: string, index: number) => {
                    if (n <= 3) return shortPreviewDateLabel(value);
                    const mid = Math.floor((n - 1) / 2);
                    return index === 0 || index === mid || index === n - 1 ? shortPreviewDateLabel(value) : '';
                  },
                }
              : previewBarSparse
                ? {
                    formatter: (value: string, index: number) => {
                      if (n <= 4) return value;
                      const mid = Math.floor((n - 1) / 2);
                      return index === 0 || index === mid || index === n - 1 ? value : '';
                    },
                  }
                : {}),
            ...(density === 'preview' && !previewTickStartMidEnd && !previewBarSparse && n > maxLabels
              ? { hideOverlap: true as const }
              : {}),
          },
    };

    const yAxisBase =
      density === 'focus'
        ? {
            type: 'value' as const,
            axisLabel: {
              fontSize: 11,
              formatter: (val: number | string) => compactAxisNumberLabel(Number(val)),
            },
          }
        : {
            type: 'value' as const,
            axisLabel: { fontSize: axisFontSize },
          };

    const opt = optionOverrides && typeof optionOverrides === 'object' ? { ...optionOverrides } : {};
    const tooltipRaw = opt.tooltip;
    delete opt.tooltip;
    const xOv = typeof opt.xAxis === 'object' && opt.xAxis ? opt.xAxis : {};
    const yOv = typeof opt.yAxis === 'object' && opt.yAxis ? opt.yAxis : {};
    delete opt.xAxis;
    delete opt.yAxis;
    delete opt.series;
    delete opt.legend;
    delete opt.grid;
    delete opt.dataZoom;

    const mergedTooltip: EChartsOption['tooltip'] =
      density === 'preview'
        ? tooltipPreview
        : tooltipRaw != null && typeof tooltipRaw === 'object'
          ? { trigger: 'axis', ...(tooltipRaw as object) }
          : { trigger: 'axis' };

    const legend: EChartsOption['legend'] = showLegend
      ? {
          bottom: density === 'focus' ? 4 : 0,
          left: 'center',
          data: series.map((s) => s.name),
          textStyle:
            density === 'preview'
              ? { fontSize: 8 }
              : axisFontSize != null
                ? { fontSize: axisFontSize }
                : undefined,
        }
      : { show: false };

    const dataZoom: EChartsOption['dataZoom'] | undefined = useDataZoom
      ? [
          { type: 'inside', xAxisIndex: 0, filterMode: 'none' },
          {
            type: 'slider',
            xAxisIndex: 0,
            height: 20,
            bottom: 8,
            filterMode: 'none',
            showDetail: false,
          },
        ]
      : undefined;

    const xMerged = {
      ...xAxisBase,
      ...xOv,
      boundaryGap: (xOv as { boundaryGap?: boolean }).boundaryGap ?? xAxisBase.boundaryGap,
    };

    return {
      tooltip: mergedTooltip,
      legend,
      grid,
      dataZoom,
      xAxis: xMerged,
      yAxis: { ...yAxisBase, ...yOv },
      series: echartsSeries,
      ...opt,
    };
  }, [xAxisData, series, showDataLabels, optionOverrides, density, compactGrid, barHighlightCategory]);

  return <EChart option={option} height={height} onEvents={onEvents} />;
};
